import React, {useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import pick from 'lodash/pick';

import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import './FindPatientForm.css';
import config from '../../config/config';
import { useGetPatientsQuery } from '../../redux/services/patientApi';
import { 
  formatPatientInitials, 
  formatPatientName,
} from '../../helpers/patients';

const getOptions = patients => (
  (patients || []).map(p => ({
    label: `${p.user.first_name} ${p.user.last_name}`,
    value: p.id,
    patient: p,
  }))
);

const handleTextChange = ({setTextInput, setQuery, limit = 100}) => e => {
  const value = get(e, 'target.value', '');
  setTextInput(value);
  setQuery({text: value, limit});
};

const FindPatientForm = props => {
  const {onSubmit, limit} = props;
  const [textInput, setTextInput] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState(props.defaultValue);
  const [query, setQuery] = useState({limit});
  const selectedPatientQuery = useGetPatientsQuery(
    {id: selectedPatientId, hide_details: props.allPatients},
    {skip: !selectedPatientId}
  );
  const selectedPatientData = selectedPatientQuery?.data?.data?.[0];
  const patientsQuery = useGetPatientsQuery(
    {...query, hide_details: props.allPatients},
    {skip: !get(query, 'text')}
  );
  const isLoading = patientsQuery.isLoading || selectedPatientQuery.isLoading;
  const textChangeHandler = handleTextChange({limit, setTextInput, setQuery});
  const debouncedTextChangeHandler = useCallback(
    debounce(textChangeHandler, 500),
    [textChangeHandler]
  );
  const patients = (get(patientsQuery, 'data.data') || []);
  const options = textInput ? getOptions(patients) : [];
  // if (selectedPatientId && !selectedPatientData) {
  //   return (
  //     <Box 
  //       style={{minWidth: props.minWidth, minHeight: props.minHeight}}
  //     />
  //   );
  // }
  if (selectedPatientId) {
    return (
      <Box sx={{minWidth: props.minWidth, minHeight: props.minHeight}}>
        <Stack 
          alignItems={props?.align || 'center'}
          padding={props.padding}
        >
          <Chip
            color={props?.disabled ? 'default' : (props?.color || 'secondary')}
            variant={props?.variant || undefined}
            label={isLoading ? 'Loading...' : formatPatientName(selectedPatientData)}
            avatar={
              <Avatar>
                {isLoading ? '' : formatPatientInitials(selectedPatientData)}
              </Avatar>
            }
            onDelete={props.disabled ? undefined : () => {
              setTextInput('');
              setSelectedPatientId(null);
              onSubmit({target: {value: null}, patientId: null}, {patientId: null});
              setQuery({});
            }}
          >
          </Chip>
        </Stack>
      </Box>
    );
  }
  return (
    <Autocomplete
      type='text'
      style={{minWidth: props.minWidth || null, maxWidth: props.maxWidth || null}}
      options={options}
      filterOptions={x => x}
      loading={isLoading}
      onChange={(e,val,reason) => {
        console.log('FindPatientForm.onChange', {e, val, reason});
        if (reason === 'selectOption') {
          if (val.id) return onSubmit({target: {value: val.id}, patientId: val.id}, {patientId: val.id});
          if (val.value) return onSubmit({target: {value: val.value}, patientId: val.value}, {patientId: val.value});
        }
        debouncedTextChangeHandler(e);
      }}
      renderInput={params => (
        <TextField
          placeholder={props.placeholder}
          onChange={debouncedTextChangeHandler}
          {...params}
          sx={props.sx}
          size={props.size}
          fullWidth={props.fullWidth}
          className={`${props.rounded && 'custom-text-field-rounded'}`}
          minWidth={props.minWidth}
          InputProps={{
            ...params.InputProps,
            autoFocus: props.autoFocus,
            startAdornment: <InputAdornment position="start">
              <SearchIcon style={{color: config().colors.primary, fontSize: '1.25rem', marginLeft: '4px'}} />
            </InputAdornment>,
            endAdornment: (
              <React.Fragment>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={(props, option, { selected }) => (
        <li><Box
          key={option.value}
          className='FindPatientFormOption-Box'
          onClick={() => {
            console.log('FindPatientForm.renderOption.onClick', option.value);
            setSelectedPatientId(option.value);
            onSubmit({patientId: option.value});
          }}
        >
          <Stack direction='row' spacing={1} padding={0.75} alignItems='center'>
            <Avatar 
              alt={formatPatientInitials(option?.patient)}
              sx={{
                width: 24,
                height: 24,
                fontSize: '0.75em',
                background: config().colors.primary,
              }}
            >
              {formatPatientInitials(option?.patient)}
            </Avatar>
            <Stack spacing={0.25}>
              <Typography variant='strong'>{option.label}</Typography>
              <Typography variant='body2' color='grey'>
                {moment(option?.patient.date_birth).format('M/D/yyyy').toString()}
              </Typography>
            </Stack>
          </Stack>
        </Box></li>
      )}
      {...pick(
        props, 
        'id', 'placeholder', 'freeSolo', 'disableClearable', 'autoFocus', 'onSubmit',
        'size',
        'fullWidth',
        'minWidth'
      )}
        />
  );

};

FindPatientForm.defaultProps = {
  allPatients: false,
  autoFocus: false,
  defaultPatientId: null,
  disableClearable: false,
  freeSolo: true,
  id: 'FindPatientForm.Autocomplete',
  placeholder: 'Search Patients',
  onSubmit: ({patientId}) => console.log('FindPatientForm.onSubmit', patientId),
  size: 'small',
  minHeight: '28px',
  fullWidth: true,
  padding: 0.5,
  limit: 100,
};

FindPatientForm.propTypes = {
  allPatients: PropTypes.bool,
  autoFocus: PropTypes.bool,
  defaultPatientId: PropTypes.string,
  disableClearable: PropTypes.bool,
  freeSolo: PropTypes.bool,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  onSubmit: PropTypes.func,
  size: PropTypes.string,
  variant: PropTypes.string, // chip variant
};

export default FindPatientForm;